/**
 * First-login dimension-value picker (DP-3188 — AC1).
 *
 * The blocking question a scoped actor answers once: which values of the active
 * record-access dimension do they work in? `useDimensionValueGate` decides
 * whether this is asked at all; this component is only the asking, and the
 * answer goes straight back to the gate's `pick`.
 *
 * **Non-dismissible by construction, but framed as part of the app.** There is
 * still no `Dialog` component, no `Escape` handler, no outside-click handler and
 * no close affordance — the blocking property comes from there being no
 * dismissal path at all, rather than from intercepting the ways out, so none can
 * be left open by accident.
 *
 * What changed after review: the first cut filled the viewport with nothing
 * behind it, and a bare full-screen page with one input and one button reads as
 * a phishing interstitial rather than as this product. It now overlays the
 * **real** dashboard — `App` renders the shell as normal and layers this on top
 * — so the app the user just logged into stays visible behind the question.
 *
 * Showing it is safe because the scope this asks about is a *view preference*,
 * not an access boundary: enforcement is server-side either way, so nothing
 * behind the scrim is anything the actor could not already see. (That is also
 * why `allAccess` actors are asked — see `useDimensionValueGate`.)
 *
 * `App` marks the shell `inert` while this is open, which is what keeps the
 * screen blocking: without it the user could tab into the sidebar and navigate
 * away without answering. That also removes the shell from the accessibility
 * tree, which is what makes the <dialog> + `aria-modal` below honest
 * rather than decorative — this card really is the only thing there.
 *
 * **The draft starts empty.** A first-login choice must be made, not merely
 * confirmed — which is also what "Continue, disabled until valid" means (PRD
 * §7). That is why the draft lives here rather than coming from
 * `useDimensionSelection`, whose draft seeds from the URL and would therefore
 * open pre-checked with everything (a paramless URL reads as all-selected). The
 * gate owns the single `useDimensionSelection` instance, so every commit
 * semantic still comes from it — through `onPick`.
 *
 * Styling stays inside the token vocabulary `RecordAccessSelector` uses (`h-9`,
 * `rounded-lg`, `bg-background`, `border border-input`), and `.is-shaking` is
 * disco-ui's own shipped shake — the same motion `MultiSelectPopover` plays when
 * a selection falls below `minSelected`, for the same rule.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  SearchInput,
  Typography,
} from '@locus-taxy/disco-ui';
import { X } from 'lucide-react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';

import type { AccessDimensionOption } from '../../api/endpoints/useAccessDimensionOptions';
import type { DimensionPick } from '../../hooks/useDimensionValueGate';
import { ALL_DIMENSION_VALUES } from '../../utils/dimensionScopeStorage';
import { TestIdAction } from '../../utils/testIdUtils';

/**
 * `data-testid`s go through `getTestId` rather than being hand-written: the ids
 * are also the Pendo element names (DSCO #277, "uniform data-testid for
 * regression and pendo tagging"), so an ad-hoc string is invisible to product
 * analytics. That matters here more than most screens — this is the first-login
 * funnel, and its drop-off is the thing worth measuring.
 */
const PICKER_PAGE = 'dimension-value-picker';
/** Ties the card's accessible name to its own heading. */
const TITLE_ID = 'dimension-value-picker-title';
const testId = (action: TestIdAction, object: string, entity?: string) =>
  getTestId({ action, object, page: PICKER_PAGE, entity });

/** At least one value must be selected to continue (PRD §7). */
const MIN_SELECTED = 1;

/**
 * How many selected values get their own chip before the rest collapse into a
 * count. A scoped actor on a large tenant can reach hundreds of values, and
 * "select all" would otherwise render a chip per value and bury the list it is
 * meant to summarise. Eight fits two comfortable rows at the card's width.
 */
const MAX_VISIBLE_CHIPS = 8;

type DimensionValuePickerProps = Readonly<{
  /** The actor's whole reach, curated server-side. */
  options: readonly AccessDimensionOption[];
  /**
   * `capitalize(activeDimension.name)`, resolved by the gate. Backend-driven
   * data: interpolated into `t()` sentences, never `t()`-wrapped itself.
   */
  label: string;
  /**
   * The gate's `pick`. Called with the committed answer — which is also where
   * `dimension_scope.picker_committed` is emitted from (PRD §9), since the
   * payload's client / actor / dimension all live on the gate.
   */
  onPick: (next: DimensionPick) => void;
}>;

/**
 * One selectable value. The colour dot takes the per-value colour `/search`
 * supplies and falls back to a neutral token when it does not — the two-tier
 * colour rule the sidebar follows (Design C). The inline `backgroundColor` is
 * wire data, not a hardcoded design value.
 */
function OptionRow({
  option,
  checked,
  onToggle,
}: Readonly<{
  option: AccessDimensionOption;
  checked: boolean;
  onToggle: (id: string) => void;
}>) {
  const inputId = `dimension-value-option-${option.id}`;

  return (
    <li>
      {/* `htmlFor` rather than nesting: the checkbox renders as a button, which
          is labelable, so the row's text is both its accessible name and its
          click target. */}
      <label
        htmlFor={inputId}
        data-testid={testId(TestIdAction.MULTI_SELECT, 'option', option.id)}
        className="flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2 hover:bg-accent"
      >
        <Checkbox
          id={inputId}
          checked={checked}
          onCheckedChange={() => onToggle(option.id)}
        />
        <span
          aria-hidden
          className="size-2.5 shrink-0 rounded-full bg-muted-foreground"
          style={option.color ? { backgroundColor: option.color } : undefined}
        />
        <span className="min-w-0 flex-1 truncate text-sm">{option.label}</span>
      </label>
    </li>
  );
}

export default function DimensionValuePicker({
  options,
  label,
  onPick,
}: DimensionValuePickerProps) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  /**
   * Whether the user has already been below the minimum *after* acting. The
   * empty draft is the starting state, so surfacing the validation before the
   * first interaction would scold them for the screen's own default; Continue
   * is disabled from the first paint either way.
   */
  const [attempted, setAttempted] = useState(false);
  /**
   * Cleared on `animationend` so a second attempt replays the motion. The
   * message is driven by the draft instead, so it stays put once the animation
   * has finished.
   */
  const [shaking, setShaking] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return options;
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(needle)
    );
  }, [options, query]);

  // Chips follow `options` order, not selection order: a stable strip is
  // scannable, whereas one that reorders as you pick makes the same value move
  // every time. Capped, with the remainder shown as a count.
  const selectedChips = useMemo(
    () =>
      options
        .filter(option => draft.has(option.id))
        .slice(0, MAX_VISIBLE_CHIPS),
    [options, draft]
  );
  const hiddenChipCount = draft.size - selectedChips.length;

  const below = draft.size < MIN_SELECTED;
  const invalid = below && attempted;

  const flagBelow = useCallback((size: number) => {
    const isBelow = size < MIN_SELECTED;
    // Dropping the last value is the only reachable "you cannot continue like
    // this" moment — Continue is genuinely `disabled` below the minimum, so
    // there is no click to react to. Same signal the sidebar's popover gives
    // for the same rule.
    setAttempted(isBelow);
    setShaking(isBelow);
  }, []);

  // Next-set computed here rather than inside a `setDraft` updater: the
  // validation flags move with the same interaction, and an updater that also
  // sets state is impure (React re-invokes updaters).
  const toggle = useCallback(
    (id: string) => {
      const next = new Set(draft);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setDraft(next);
      flagBelow(next.size);
    },
    [draft, flagBelow]
  );

  // Acts on what is on screen: with a filter applied, a "Select all" that also
  // took hidden rows would be a silent over-selection. Unfiltered — the
  // first-login default — that is every option, which is what makes the
  // all-selected answer reachable in one click.
  const selectAllVisible = useCallback(() => {
    const next = new Set(draft);
    for (const option of filtered) {
      next.add(option.id);
    }
    setDraft(next);
    flagBelow(next.size);
  }, [draft, filtered, flagBelow]);

  const clearAll = useCallback(() => {
    setDraft(new Set());
    flagBelow(0);
  }, [flagBelow]);

  const submit = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault();
      if (draft.size < MIN_SELECTED) {
        flagBelow(draft.size);
        return;
      }
      // Covering every reachable value is the all-selected state, which is a
      // statement about the dimension rather than a snapshot of today's rows:
      // hand back the sentinel so a value added later stays in scope, and so
      // the URL stays paramless (Q9).
      onPick(draft.size === options.length ? ALL_DIMENSION_VALUES : draft);
    },
    [draft, options.length, onPick, flagBelow]
  );

  return (
    <Box
      data-testid={testId(TestIdAction.VIEW, 'container')}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      {/* Scrim over the live dashboard. Light enough that the app stays legible
          underneath — that recognition is the whole point of overlaying rather
          than replacing — and dark enough that focus lands on the card. */}
      <Box
        aria-hidden
        className="absolute inset-0 bg-background/60 backdrop-blur-[2px]"
      />

      {/* A native <dialog> rather than a hand-asserted role. Rendered `open`
          rather than via `showModal()` because the modality is already real:
          App marks the rest of the tree `inert` while the gate is asking, and
          the underlay is `aria-hidden`. Deliberately not a `Dialog` component —
          non-dismissible by construction, so there is no Escape handler, no
          outside-click handler and no close affordance to get wrong. */}
      <dialog
        open
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        // `flex flex-col overflow-hidden` is load-bearing, not styling. A
        // <dialog> is display:block, so without it the form is a block child
        // that sizes to its content: the option list's `flex-1 min-h-0
        // overflow-y-auto` has no constrained parent to scroll within, grows to
        // the full list height, and pushes the submit button past the fold.
        // On a tenant with 300+ values the gate becomes unpassable.
        className="relative z-10 m-0 flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-lg border border-border bg-card p-6 text-inherit shadow-lg"
      >
        <form
          onSubmit={submit}
          data-testid={testId(TestIdAction.SUBMIT, 'form')}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <Box className="flex flex-col gap-1">
            <Typography
              variant="h5"
              component="h1"
              id={TITLE_ID}
              data-testid={testId(TestIdAction.VIEW, 'title')}
            >
              {t(
                'dashboard.dimensionValuePicker.title',
                'Which {{label}} do you want to work in?',
                { label }
              )}
            </Typography>
            <Typography variant="body2" color="muted">
              {t(
                'dashboard.dimensionValuePicker.description',
                'We remember this for next time. You can change it whenever you like from the sidebar.'
              )}
            </Typography>
          </Box>

          <SearchInput
            value={query}
            onChange={setQuery}
            data-testid={testId(TestIdAction.SEARCH, 'input')}
            aria-label={t(
              'dashboard.dimensionValuePicker.searchLabel',
              'Search {{label}} values',
              { label }
            )}
            placeholder={t(
              'dashboard.dimensionValuePicker.searchPlaceholder',
              'Search'
            )}
          />

          <Box className="flex items-center justify-between gap-2">
            <Typography
              variant="body2"
              color="muted"
              data-testid={testId(TestIdAction.VIEW, 'selected-count')}
            >
              {t(
                'dashboard.dimensionValuePicker.selectedCount',
                '{{selected}} of {{total}} selected',
                { selected: draft.size, total: options.length }
              )}
            </Typography>
            <Box className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={testId(
                  TestIdAction.MULTI_SELECT,
                  'select-all-button'
                )}
                onClick={selectAllVisible}
              >
                {t('dashboard.dimensionValuePicker.selectAll', 'Select all')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={testId(TestIdAction.MULTI_SELECT, 'clear-button')}
                onClick={clearAll}
              >
                {t('dashboard.dimensionValuePicker.clear', 'Clear')}
              </Button>
            </Box>
          </Box>

          {/* Selected values stay visible no matter how far the list is scrolled
            or how the search narrows it — with a few hundred options, a
            scattered selection is otherwise invisible and only re-findable by
            hunting. Deliberately NOT done by sorting selected rows to the top:
            that moves a row out from under the cursor at the moment of
            clicking it, which is worse than the problem. */}
          {draft.size > 0 && (
            <Box
              data-testid={testId(TestIdAction.VIEW, 'selected-chips')}
              className="flex flex-wrap gap-1.5"
            >
              {selectedChips.map(option => (
                <Chip
                  key={option.id}
                  size="sm"
                  variant="subtle"
                  color="primary"
                  accentColor={option.color}
                  onClick={() => toggle(option.id)}
                  data-testid={testId(
                    TestIdAction.MULTI_SELECT,
                    'selected-chip',
                    option.id
                  )}
                  aria-label={t(
                    'dashboard.dimensionValuePicker.removeSelected',
                    'Remove {{value}}',
                    { value: option.label }
                  )}
                >
                  {option.label}
                  <X aria-hidden />
                </Chip>
              ))}
              {hiddenChipCount > 0 && (
                <Chip
                  size="sm"
                  variant="outlined"
                  data-testid={testId(
                    TestIdAction.VIEW,
                    'selected-chip-overflow'
                  )}
                >
                  {t(
                    'dashboard.dimensionValuePicker.moreSelected',
                    '+{{count}} more',
                    { count: hiddenChipCount }
                  )}
                </Chip>
              )}
            </Box>
          )}

          <ul
            data-testid={testId(TestIdAction.MULTI_SELECT, 'option-list')}
            data-invalid={invalid || undefined}
            // Filtered on the animation's own name, the way disco-ui's
            // `MultiSelectPopover` does it: `animationend` bubbles, so any
            // animation finishing on a row inside would otherwise clear the shake
            // mid-play and swallow the signal.
            onAnimationEnd={event => {
              if (event.animationName === 'field-shake') {
                setShaking(false);
              }
            }}
            className={`min-h-0 flex-1 overflow-y-auto rounded-lg border bg-background p-1 ${
              invalid ? 'border-destructive' : 'border-input'
            } ${shaking ? 'is-shaking' : ''}`}
          >
            {filtered.length === 0 ? (
              <li className="px-2 py-3 text-sm text-muted-foreground">
                {t(
                  'dashboard.dimensionValuePicker.noMatches',
                  'No {{label}} values match your search',
                  { label }
                )}
              </li>
            ) : (
              filtered.map(option => (
                <OptionRow
                  key={option.id}
                  option={option}
                  checked={draft.has(option.id)}
                  onToggle={toggle}
                />
              ))
            )}
          </ul>

          {invalid && (
            <Typography
              variant="body2"
              color="destructive"
              role="alert"
              data-testid={testId(TestIdAction.VIEW, 'error-message')}
            >
              {t(
                'dashboard.dimensionValuePicker.minSelected',
                'Select at least one {{label}} to continue',
                { label }
              )}
            </Typography>
          )}

          <Button
            type="submit"
            disabled={below}
            data-testid={testId(TestIdAction.SUBMIT, 'continue-button')}
          >
            {t('dashboard.dimensionValuePicker.continue', 'Continue')}
          </Button>
        </form>
      </dialog>
    </Box>
  );
}
